import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/button/button';
import { Checkbox } from '@/components/checkbox/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Spinner } from '@/components/spinner/spinner';
import { getListUserQueryOptions } from '@/features/users/api/get-list-user.api';

export interface ProductClaimUserSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export default function ProductClaimUserSelector({
  value,
  onChange,
}: ProductClaimUserSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const usersQuery = useQuery({
    ...getListUserQueryOptions({ limit: 100, search }),
    enabled: open,
  });
  const users = usersQuery.data?.result.data ?? [];

  const toggle = (email: string) => {
    onChange(
      value.includes(email) ? value.filter(v => v !== email) : [...value, email]
    );
  };

  return (
    <div className="grid gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(email => (
            <span key={email} className="px-2 py-1 bg-muted rounded-md text-xs">
              {email}
            </span>
          ))}
        </div>
      )}
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        {value.length > 0 ? 'Ubah User' : 'Pilih User'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih User</DialogTitle>
            <DialogDescription>
              Pilih user yang diizinkan mengklaim produk ini.
            </DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Cari nama atau email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className="max-h-[300px] overflow-y-auto">
            {usersQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Tidak ada user ditemukan
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {users.map(user => {
                  const checked = value.includes(user.email);
                  return (
                    <button
                      key={user.id_user}
                      type="button"
                      onClick={() => toggle(user.email)}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
                    >
                      <Checkbox
                        checked={checked}
                        onClick={e => e.preventDefault()}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{user.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>
              Selesai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
